import axios from 'axios';

// Create axios instance for our backend API
const api = axios.create({
  baseURL: '/thumbgen/api', // This will proxy to our backend via the proxy setting
});

// Helper function to convert image URL to Blob
const imageUrlToBlob = async (imageUrl) => {
  try {
    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }
    const blob = await response.blob();
    return blob;
  } catch (error) {
    console.error('Error converting image to blob:', error);
    throw error;
  }
};

// Generate base thumbnail using flux-kontext-pro or kontext-max (via backend)
export const generateBaseThumbnail = async (imageUrl, customTitle, additionalPrompt = '') => {
  try {
    console.log('[DEBUG] Calling backend for thumbnail generation...');
    
    const response = await api.post('/generate-thumbnail', {
      imageUrl,
      customTitle,
      additionalPrompt,
    });
    
    if (response.data.success) {
      return response.data.output;
    } else {
      throw new Error(response.data.error || 'Failed to generate thumbnail');
    }
  } catch (error) {
    console.error('Error generating base thumbnail:', error);
    throw error.response?.data?.error ? new Error(error.response.data.error) : error;
  }
};

// Remove background using rembg (via backend)
export const removeBackground = async (imageUrl) => {
  try {
    console.log('[DEBUG] Calling backend for background removal...');
    
    const response = await api.post('/remove-background', {
      imageUrl,
    });
    
    if (response.data.success) {
      return response.data.output;
    } else {
      throw new Error(response.data.error || 'Failed to remove background');
    }
  } catch (error) {
    console.error('Error removing background:', error);
    throw error.response?.data?.error ? new Error(error.response.data.error) : error;
  }
};

// Generate base thumbnail from uploaded file (via backend)
export const generateBaseThumbnailFromFile = async (file, customTitle, additionalPrompt = '') => {
  try {
    console.log('[DEBUG] Calling backend for thumbnail generation from file...');
    
    const formData = new FormData();
    formData.append('image', file);
    formData.append('customTitle', customTitle);
    if (additionalPrompt) {
      formData.append('additionalPrompt', additionalPrompt);
    }
    
    const response = await api.post('/generate-thumbnail-file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    if (response.data.success) {
      return response.data.output;
    } else {
      throw new Error(response.data.error || 'Failed to generate thumbnail');
    }
  } catch (error) {
    console.error('Error generating base thumbnail from file:', error);
    throw error.response?.data?.error ? new Error(error.response.data.error) : error;
  }
};

// Remove background from uploaded file (via backend)
export const removeBackgroundFromFile = async (file) => {
  try {
    console.log('[DEBUG] Calling backend for background removal from file...');
    
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await api.post('/remove-background-file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    if (response.data.success) {
      return response.data.output;
    } else {
      throw new Error(response.data.error || 'Failed to remove background');
    }
  } catch (error) {
    console.error('Error removing background from file:', error);
    throw error.response?.data?.error ? new Error(error.response.data.error) : error;
  }
};

// Health check function
export const checkBackendHealth = async () => {
  try {
    const response = await api.get('/health');
    return response.data;
  } catch (error) {
    console.error('Backend health check failed:', error);
    throw error;
  }
};

// Legacy functions kept for compatibility (no longer needed)
export const getPredictionStatus = async (predictionId) => {
  console.warn('getPredictionStatus not needed with backend proxy');
  return null;
};

export const pollPrediction = async (result, onProgress = null) => {
  console.warn('pollPrediction not needed with backend proxy');
  return result;
};
